/**
 * A subject's tree, and the node selection that rides in the URL.
 *
 * Its own module with no React and no router import — CRA's jest cannot resolve
 * react-router-dom v7 or react-dnd, both ESM-only, so anything sharing a file
 * with them is untestable. The rules here have no visible failure mode, which
 * is exactly the kind that needs a test.
 *
 * ONE KIND OF NODE. This used to weave two lists — categories and topics — into
 * a tree with synthetic keys, because their ids came from separate sequences
 * and category 5 was not topic 5. Migration 108 made a category a topic with no
 * parent, so there is one list, one id space, and the key IS the id.
 */
import { collectDescendantIds } from '../components/common/treeHelpers';
import type { Topic } from '../types';

/** One rail row. */
export interface SubjectNode {
  id: number;
  parentId: number | null;
  name: string;
  /** What this node's id was as a category, so old `?category=N` links resolve. */
  legacyCategoryId: number | null;
}

/** Flatten the subject's nodes into a parent-chain list. Exported for tests. */
export function buildSubjectTree(topics: Topic[]): SubjectNode[] {
  return topics.map((t) => ({
    id: t.id,
    // `parent_id` is the column; `category_id` is its legacy spelling and is
    // only ever a root's id. Falling back covers a response from either.
    parentId: t.parent_id ?? t.category_id ?? null,
    name: t.name,
    legacyCategoryId: t.legacy_category_id ?? null,
  }));
}

/**
 * Which node `?node=…` means.
 *
 * Three spellings reach this. `12` is what the app writes now. `topic-12` is
 * the old spelling of the same id. `category-2` is the old spelling of a
 * LEGACY id, which is a different number from the node's — those links are in
 * bookmarks and in the /projects redirect, so they are resolved against the
 * tree rather than dropped.
 *
 * `category-N` FALLS BACK TO READING N AS A NODE ID, and that is not
 * belt-and-braces. The `/categories` shim reports `legacy_category_id or
 * topic.id`, so a root created SINCE migration 108 has no legacy id and is
 * reported under its own node id — and every `category-` link the settings
 * screens build for one of those carried a number with no legacy match. The
 * lookup returned null, which the workspace renders as "Everything in this
 * subject", so the link looked like it worked and quietly selected nothing.
 *
 * The two id spaces cannot collide: legacy ids stop at 152 and post-migration
 * topic ids start above 774 (see `jcseo/api/categories.py`), so a legacy match
 * and a node match are never both plausible for the same number.
 */
export function parseNodeParam(
  raw: string | null,
  nodes: SubjectNode[] = [],
): number | null {
  if (!raw) return null;
  const [head, rest] = raw.includes('-') ? raw.split('-') : [null, raw];
  const id = Number(rest);
  if (!Number.isFinite(id)) return null;
  if (head === 'category') {
    const match = nodes.find((n) => n.legacyCategoryId === id);
    if (match) return match.id;
    // No legacy match: a root made after the migration, addressed by its own id.
    return nodes.some((n) => n.id === id) ? id : null;
  }
  if (head !== null && head !== 'topic') return null;
  return id;
}

/** A to Z by name: the order of the outline rail (DraggableTree's default),
 *  so every list of a subject's sections reads the same. */
export function byName(a: SubjectNode, b: SubjectNode): number {
  return a.name.localeCompare(b.name);
}

/**
 * The nodes directly under a selection, or the roots when nothing is selected,
 * A to Z like the rail beside them.
 *
 * The panes are still per-node, so selecting something with children offers a
 * way onward rather than rendering an empty pane pretending to be a view.
 */
export function childTopicsOf(
  nodes: SubjectNode[],
  selected: SubjectNode | null,
): SubjectNode[] {
  const parentId = selected ? selected.id : null;
  return nodes.filter((n) => n.parentId === parentId).sort(byName);
}

/**
 * The chain from the root down to `node`, exclusive of the node itself.
 *
 * Two callers, both about not losing the user: the header shows it as a path,
 * because a bare node name gives no clue which branch you are in; and the rail
 * opens it, because a tree that arrives collapsed hides the very node the URL
 * asked for.
 */
export function ancestorsOf(
  nodes: SubjectNode[],
  node: SubjectNode | null,
): SubjectNode[] {
  if (!node) return [];
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const chain: SubjectNode[] = [];
  const seen = new Set<number>([node.id]);
  let parentId = node.parentId;
  while (parentId != null) {
    const parent = byId.get(parentId);
    // A missing parent, or a cycle, stops the walk rather than looping forever
    // — the chain so far is still worth showing.
    if (!parent || seen.has(parent.id)) break;
    seen.add(parent.id);
    chain.unshift(parent);
    parentId = parent.parentId;
  }
  return chain;
}

/** A node as one row of a flat pick list: tree order, with its depth. */
export interface PickRow {
  node: SubjectNode;
  depth: number;
}

/**
 * The tree as an indented list you can pick one row from.
 *
 * WHY THIS SHAPE. Attaching research to a subject was three dropdowns —
 * project, then category, then topic — which could only ever reach the third
 * level. The tree has no fixed depth any more, so the picker cannot either, and
 * a nested list of selects would be worse than the drill-down it replaced.
 * One flat list in tree order, indentation carrying the structure.
 *
 * FILTERING KEEPS ANCESTORS. Matching rows alone would show a node floating
 * with no idea which section it sits in — and two nodes called "Pricing" under
 * different sections would be indistinguishable. So a match pulls its whole
 * ancestor chain along, shown as context rather than as matches.
 */
export function pickRows(nodes: SubjectNode[], query: string): PickRow[] {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const childrenOf = new Map<number | null, SubjectNode[]>();
  for (const n of nodes) {
    const siblings = childrenOf.get(n.parentId) ?? [];
    siblings.push(n);
    childrenOf.set(n.parentId, siblings);
  }
  for (const siblings of Array.from(childrenOf.values())) {
    siblings.sort(byName);
  }

  const q = query.trim().toLowerCase();
  let keep: Set<number> | null = null;
  if (q) {
    keep = new Set<number>();
    for (const n of nodes) {
      if (!n.name.toLowerCase().includes(q)) continue;
      let cur: SubjectNode | undefined = n;
      const guard = new Set<number>();
      while (cur && !guard.has(cur.id)) {
        guard.add(cur.id);
        keep.add(cur.id);
        cur = cur.parentId === null ? undefined : byId.get(cur.parentId);
      }
    }
  }

  const rows: PickRow[] = [];
  const walk = (parentKey: number | null, depth: number) => {
    for (const child of childrenOf.get(parentKey) ?? []) {
      if (keep && !keep.has(child.id)) continue;
      rows.push({ node: child, depth });
      walk(child.id, depth + 1);
    }
  };
  walk(null, 0);
  return rows;
}

/** One step of the path above the node you are looking at. */
export interface Crumb {
  key: string;
  label: string;
  /** THE LIST leaves the subject; a NODE moves inside it. The component turns
   *  this into an href — this module stays free of the router. */
  target: { kind: 'list' } | { kind: 'node'; id: number | null };
}

/**
 * THE WAY BACK OUT, and the way up.
 *
 * The workspace had no exit. Its two paths — the rail's "SUBJECT / Trucking" and this
 * trail — were both plain text, and the top nav's Subjects tab is deliberately STICKY:
 * it returns you to the subject you were last in, which is the opposite of leaving. So
 * the only ways back to the list were the browser's Back button and typing the URL.
 *
 * The trail starts at the app root for that reason. `Subjects › Trucking › case-studies`
 * reads as one path from the top down to the node, and every step of it is somewhere you
 * can click — where the old version began at `Trucking`, a name that read as a label
 * because nothing above it was named.
 *
 * The SELECTED node is deliberately absent: it is the <h1> directly below, and a crumb
 * for where you already are is a link to the page you are on. That is why `selected` is
 * a parameter and not just its ancestors — with nothing selected the subject ROOT is
 * what you are looking at ("Everything in this subject"), and the same rule applies to
 * it. An empty ancestor list cannot tell those two cases apart on its own.
 */
export function subjectCrumbs(
  projectName: string | null | undefined,
  ancestors: SubjectNode[],
  selected: SubjectNode | null,
): Crumb[] {
  const crumbs: Crumb[] = [{ key: 'list', label: 'Subjects', target: { kind: 'list' } }];
  // NOT while the subject is still loading (a crumb reading "…" is a link to nowhere),
  // and NOT when nothing is selected — with no node, the subject root IS the selection,
  // and its crumb would be a link to the page you are on. Same rule as the node itself.
  if (projectName && selected) {
    // `id: null` is the subject ROOT — "Everything in this subject", which is what
    // clearing `?node=` selects. It is a real destination, not an absence.
    crumbs.push({
      key: 'root',
      label: projectName,
      target: { kind: 'node', id: null },
    });
  }
  for (const a of ancestors) {
    crumbs.push({ key: `n${a.id}`, label: a.name, target: { kind: 'node', id: a.id } });
  }
  return crumbs;
}

/**
 * Where the selection should land once `deleted` is gone — `undefined` meaning LEAVE IT
 * ALONE, and `null` meaning the subject root.
 *
 * Delete cascades down the parent chain, so removing a node three levels above the one
 * you are reading takes it with it. Nothing on screen would say so: the URL would still
 * carry the dead id, `nodes.find` would come back empty, and the pane would quietly
 * render "Everything in this subject" as though you had asked for it.
 *
 * The other half matters just as much — deleting a node you are NOT inside must not
 * move you. Tidying up a branch elsewhere in the tree while reading one node is the
 * common case, and a delete that jumps you somewhere else each time makes the rail feel
 * like it is fighting you.
 */
export function selectionAfterDelete(
  nodes: SubjectNode[],
  deleted: SubjectNode,
  selectedId: number | null,
): number | null | undefined {
  if (selectedId === null) return undefined;
  if (selectedId !== deleted.id) {
    const doomed = collectDescendantIds(
      nodes,
      deleted.id,
      (n) => n.id,
      (n) => n.parentId,
    );
    if (!doomed.has(selectedId)) return undefined;
  }
  // Up one, not out to the root: you were working somewhere, and the parent is the
  // nearest thing to where you were that still exists.
  return deleted.parentId;
}


/**
 * The name a rename should actually PUT, or `null` for "don't".
 *
 * Three ways a rename ends in nothing, and none of them is an error worth
 * showing: the box was emptied (a nameless row is an unclickable one, so an
 * empty box means "no" — Escape and a cleared field agree), the name came back
 * the same (the box saves on blur, so merely clicking into it and away again
 * would otherwise write a row and refetch the tree), or the node went away
 * while the box was open.
 *
 * Trimming here rather than at the call site is the point of the split: it is
 * the same rule the draft box follows, and a name saved with a trailing space
 * sorts and matches differently from the one you typed.
 */
export function renamedName(
  nodes: SubjectNode[],
  id: number,
  raw: string,
): string | null {
  const name = raw.trim();
  if (!name) return null;
  const before = nodes.find((n) => n.id === id);
  if (!before || before.name === name) return null;
  return name;
}

/**
 * The id a not-yet-created node uses while you are naming it.
 *
 * NEGATIVE, so it cannot collide with a real Topic id, and a constant rather
 * than a counter because only one draft exists at a time — pressing + on a
 * second row moves the draft, it does not open two.
 *
 * A real row in the tree rather than a form above it: the node appears where it
 * is going to live, under the parent you pressed +@ on, so you can see the
 * shape you are making while you type the name.
 */
export const DRAFT_NODE_ID = -1;

export function isDraftNode(node: { id: number }): boolean {
  return node.id === DRAFT_NODE_ID;
}

/**
 * Insert the draft under `parentId`, or at the root when null.
 *
 * Returns a NEW array — the tree component memoizes on identity, and mutating
 * would leave the draft invisible until something else happened to re-render.
 */
export function withDraftNode(
  nodes: SubjectNode[],
  parentId: number | null,
): SubjectNode[] {
  return [
    ...nodes,
    {
      id: DRAFT_NODE_ID,
      parentId,
      name: '',
      legacyCategoryId: null,
    },
  ];
}

/**
 * Can this node be dragged, or dropped onto?
 *
 * The draft cannot: it has no row on the server, so a reparent naming it would
 * PUT a negative id — a 404 at best, and at worst a real node moved under
 * something that does not exist.
 */
export function isDraggableNode(node: { id: number }): boolean {
  return !isDraftNode(node);
}

/**
 * Whether the URL is asking for the structure planner to be open on arrival.
 *
 * Two spellings, both meaning "I came here for the planner":
 *
 *   `?plan=1`    — what "Start a subject" on the Trends page writes. The planner
 *                  is already thinking about your keywords when you land, so a
 *                  closed drawer would hide the only thing happening and show
 *                  you the empty tree instead.
 *   `?tab=plan`  — the older bookmark, from when Plan was a tab on this screen.
 *                  The tab whitelist stops it blanking the page, but landing on
 *                  Evidence with the planner shut still answers the wrong
 *                  question.
 *
 * A function rather than two inline comparisons because there are now two
 * spellings and a third would otherwise be added in one place and forgotten in
 * the other — which is how `?tab=plan` came to half-work in the first place.
 */
export function wantsPlanner(params: {
  get(key: string): string | null;
}): boolean {
  return params.get('plan') === '1' || params.get('tab') === 'plan';
}
